import { memo, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  faCircleNodes,
  faSearch,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';

import { AdminSection, ModalProps, NumericString } from '@typings';
import { addClusterOrganization, editClusterOrganization } from '@services';
import { toastifyResponseError } from '@utils';

import {
  Button,
  Field,
  HighlightExcerpt,
  Icon,
  Modal,
  Render,
} from '@components';
import { EmptyContent } from '@components/Ui';

const schema = z.object({
  quota: z.object({
    totalRunningJobs: z
      .string()
      .refine(
        (val) => val === '' || (!Number.isNaN(Number(val)) && Number(val) >= 0),
        {
          message: 'Jobs must be a positive number',
        },
      ),
  }),
});

const MAX_SHOW_ORGS = 25;

type Schema = z.infer<typeof schema>;

type Props = ModalProps & {
  clusterName: AdminSection.ClusterOrganization['orgName'];
  org?: AdminSection.ClusterOrganization;
  orgs?: AdminSection.ClusterOrganization[];
  onOrgAdd?: (org: AdminSection.ClusterOrganization) => void;
  onOrgUpdate?: (org: AdminSection.ClusterOrganization) => void;
};

export const ManageClusterOrganizationModal = memo<Props>(
  ({ clusterName, org, orgs = [], closeModal, onOrgAdd, onOrgUpdate }) => {
    const [search, setSearch] = useState('');
    const [selectedOrg, setSelectedOrg] =
      useState<AdminSection.ClusterOrganization | null>(null);

    const {
      register,
      handleSubmit,
      formState: { isSubmitting: isAdding, errors },
    } = useForm<Schema>({
      resolver: zodResolver(schema),
      defaultValues: {
        quota: {
          totalRunningJobs: org?.quota?.totalRunningJobs ?? '',
        },
      },
    });

    const filteredOrgs = useMemo(() => {
      if (search.length < 2) {
        return orgs;
      }

      const result = [];
      const searchLower = search.toLowerCase();

      for (let i = 0; i < orgs.length; i += 1) {
        const org = orgs[i];

        if (org.orgName.toLowerCase().includes(searchLower)) {
          result.push(org);
        }

        if (result.length >= MAX_SHOW_ORGS) {
          break;
        }
      }

      return result;
    }, [orgs, search]);

    const handleOrganizationNameSubmit = handleSubmit(async (data) => {
      const orgName = selectedOrg?.orgName ?? org?.orgName;

      if (!orgName) {
        return;
      }

      const newOrg: AdminSection.ClusterOrganization = {
        orgName,
      };

      if (data.quota.totalRunningJobs) {
        newOrg.quota = {
          totalRunningJobs: String(
            data.quota.totalRunningJobs,
          ) as NumericString,
        };
      }

      try {
        if (org) {
          await editClusterOrganization({
            clusterName,
            ...newOrg,
          });
          onOrgUpdate?.(newOrg);
        } else {
          await addClusterOrganization({
            clusterName,
            ...newOrg,
          });
          onOrgAdd?.(newOrg);
        }

        closeModal?.();
      } catch (error) {
        toastifyResponseError(error);
      }
    });

    const renderOrgs = () => {
      if (search.length < 2) {
        return (
          <EmptyContent
            className="py-6"
            icon={faCircleNodes}
            title="Type at least 2 characters"
          />
        );
      }

      if (filteredOrgs.length === 0) {
        return (
          <EmptyContent
            className="py-6"
            icon={faSearch}
            title="No organizations found"
          />
        );
      }

      return filteredOrgs.map((org) => {
        const isSelected = org.orgName === selectedOrg?.orgName;

        return (
          <Button
            key={org.orgName}
            theme
            className={clsx(
              'flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background',
              { 'bg-background': isSelected },
            )}
            onClick={() => setSelectedOrg(org)}
          >
            <div className="text-left">
              <HighlightExcerpt search={search} text={org.orgName} />
            </div>
          </Button>
        );
      });
    };

    const orgName = selectedOrg?.orgName ?? org?.orgName;

    return (
      <Modal.Content
        title={org ? 'Edit Organization' : 'Add Organization'}
        subtitle={
          <>
            {org ? 'Edit' : 'Add'} organization to <b>{clusterName}</b> cluster
          </>
        }
        className="w-[640px]"
      >
        <form onSubmit={handleOrganizationNameSubmit}>
          <Render if={!orgName}>
            <Field.Input
              value={search}
              required
              label="Organization Name"
              note="Search by name"
              containerClassName="mb-4"
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedOrg(null);
              }}
            />

            <div className="mt-6 flex h-[240px] flex-col gap-4 overflow-auto">
              {renderOrgs()}
            </div>
          </Render>

          <Render if={orgName}>
            <div className="relative">
              <Field.Input
                required
                readOnly
                value={orgName}
                label="Organization Name"
                containerClassName="mb-4"
              />

              <Render if={selectedOrg}>
                <Icon
                  icon={faXmark}
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-[20px] opacity-50 transition-opacity hover:opacity-100"
                  onClick={() => {
                    setSelectedOrg(null);
                    setSearch('');
                  }}
                />
              </Render>
            </div>

            <Field.Input
              {...register('quota.totalRunningJobs')}
              label="Total Running Jobs Quota"
              type="number"
              className="w-full"
              containerClassName="flex-1"
              error={errors.quota?.totalRunningJobs?.message}
            />
          </Render>
        </form>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            loading={isAdding}
            className="px-10 capitalize"
            disabled={!orgName}
            onClick={handleOrganizationNameSubmit}
          >
            {org ? 'Save' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    );
  },
);

ManageClusterOrganizationModal.displayName = 'ManageClusterOrganizationModal';
